import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { useAppSelector } from "../../hooks/hooks";
import Leaderboard from "./Leaderboard";
import ScrollableSection from "./ScrollableSection";
import { CardContainer } from "./CardContainer";
import { SectionContainer } from "./SectionContainer";
import { Header } from "./Header";
import Invitation from "./Invitation";
const categories = [
  {
    key: "most_popular",
    title: "Most Popular Today",
    icon: "/png/MostPopular.png",
  },
  {
    key: "topup",
    title: "Direct Game Topup",
    icon: "/png/DirectGameTopUp.png",
  },
  {
    key: "game_cards",
    title: "Game Cards",
    icon: "/png/GameCards.png",
  },
  {
    key: "gift_cards",
    title: "Gift Cards",
    icon: "/png/GiftCards.png",
  },
  {
    key: "dg_coins",
    title: "DG Coins",
    icon: "/png/dg.png",
  },
  { key: "data", title: "Data Services", icon: "/png/PrepaidServices.png" },
  {
    key: "airtime",
    title: "Airtime Services",
    icon: "/png/PrepaidServices.png",
  },
  { key: "cable", title: "Cable TV", icon: "/png/CableTV.png" },
  {
    key: "electric",
    title: "Electric Payment",
    icon: "/png/ElectricityPayment.png",
  },
];

const Main = () => {
  const location = useLocation();
  const { data: appData } = useAppSelector((state) => state.appData);

  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    if (location.state?.scrollTo && !isScrolled) {
      const scrollToSection = () => {
        const sectionId = `section-${location.state.scrollTo}`;
        const sectionElement = document.getElementById(sectionId);

        if (sectionElement) {
          sectionElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
          setIsScrolled(true);
          return true;
        }
        return false;
      };

      // Try immediately
      if (!scrollToSection()) {
        // If not found, try again after a short delay (components might still be rendering)
        const timer = setTimeout(() => {
          scrollToSection();
        }, 300);

        return () => clearTimeout(timer);
      }
    }
  }, [location.state, isScrolled, appData]); // Add appData to dependencies

  // Reset scroll state when location changes
  useEffect(() => {
    setIsScrolled(false);
  }, [location.state]);

  const getProvidersWithDiscounts = (providers: any[], plans: any[]) => {
    return providers.map((provider) => {
      const providerPlans = plans.filter(
        (plan: {
          category: { provider_id: any; provider: { id: any } };
          countries: number[];
        }) => {
          const matchesProvider =
            plan.category?.provider_id === provider.id ||
            plan.category?.provider?.id === provider.id;
          return matchesProvider;
        },
      );

      const discounts = providerPlans.map(
        (plan: { discount: string; wallet_price: string }) => {
          const numericDiscount = Number(plan.discount) || 0;
          const numericWalletPrice = Number(plan.wallet_price) || 0;
          const hasDiscount =
            numericDiscount > 0 && numericDiscount > numericWalletPrice;

          const discountPercent = hasDiscount
            ? ((numericDiscount - numericWalletPrice) / numericDiscount) * 100
            : 0;
          return discountPercent;
        },
      );

      const maxDiscount = discounts.length > 0 ? Math.max(...discounts, 0) : 0;

      return {
        ...provider,
        discount: maxDiscount,
      };
    });
  };

  return (
    <>
      {/* Desktop View */}
      <div className="hidden bp:block bp:col-14 bp:pt-10 bp:pb-2 min-w-[300px]">
        {categories.map(({ key, title, icon }) => {
          const providers = (appData?.[key]?.providers ?? []).slice(0, 6);
          const plans = appData?.[key]?.plans ?? [];
          const providersWithDiscount = getProvidersWithDiscounts(
            providers,
            plans,
          );
          return (
            providers &&
            providers.length > 0 && (
              <ScrollableSection
                key={key}
                providers={providersWithDiscount}
                icon={icon}
                title={title}
                viewAllUrl={key}
              />
            )
          );
        })}
      </div>

      {/* Mobile View */}
      <div className="block bp:hidden col-6 px-4 pt-10 pb-2">
        <Leaderboard />
        {categories.map(({ key, title, icon }) => {
          const providers = (appData?.[key]?.providers ?? []).slice(0, 6);
          const plans = appData?.[key]?.plans ?? [];
          const providersWithDiscount = getProvidersWithDiscounts(
            providers,
            plans,
          );
          return (
            providers &&
            providers.length > 0 && (
              <SectionContainer key={key} id={`section-${key}`}>
                <Header icon={icon} title={title} viewAllUrl={key} />
                <CardContainer cardData={providersWithDiscount} url={key} />
              </SectionContainer>
            )
          );
        })}

        <Invitation />
      </div>
    </>
  );
};

export default Main;
